// ScheduledReports.js — Agendamento de relatórios por e-mail (admin, Fase 3 item 18).
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from './config';
import './ScheduledReports.css';

const DIAS = [
  { v: 0, label: 'Domingo' },
  { v: 1, label: 'Segunda' },
  { v: 2, label: 'Terça' },
  { v: 3, label: 'Quarta' },
  { v: 4, label: 'Quinta' },
  { v: 5, label: 'Sexta' },
  { v: 6, label: 'Sábado' }
];

const FORM_VAZIO = {
  issabel_server_id: '',
  email: '',
  filas: '',
  frequencia: 'daily',
  hora: '06:00',
  dia_semana: 1,
  ativo: true
};

function ScheduledReports({ token }) {
  const [schedules, setSchedules] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/scheduled-reports`, { headers: authHeaders });
      setSchedules(resp.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    axios
      .get(`${API_URL}/users/servers/active`, { headers: authHeaders })
      .then((r) => setServers(r.data || []))
      .catch(() => {});
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    try {
      if (editingId) {
        await axios.put(`${API_URL}/scheduled-reports/${editingId}`, form, { headers: authHeaders });
        setMsg('Agendamento atualizado com sucesso');
      } else {
        await axios.post(`${API_URL}/scheduled-reports`, form, { headers: authHeaders });
        setMsg('Agendamento criado com sucesso');
      }
      setForm(FORM_VAZIO);
      setEditingId(null);
      fetchSchedules();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar agendamento');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    try {
      await axios.delete(`${API_URL}/scheduled-reports/${id}`, { headers: authHeaders });
      fetchSchedules();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir');
    }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({
      issabel_server_id: String(s.issabel_server_id),
      email: s.email,
      filas: s.filas || '',
      frequencia: s.frequencia,
      hora: s.hora,
      dia_semana: s.dia_semana != null ? Number(s.dia_semana) : 1,
      ativo: Boolean(s.ativo)
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(FORM_VAZIO);
  };

  return (
    <div className="scheduledReportsContainer">
      <h1 className="pageTitle">Relatórios Agendados</h1>
      <p className="scheduledHint">
        Envie relatórios de filas por e-mail automaticamente (diário ou semanal). Requer SMTP configurado no backend.
      </p>

      {error && <div className="message messageError">{error}</div>}
      {msg && <div className="message messageSuccess">{msg}</div>}

      <form className="scheduledForm" onSubmit={submit}>
        <div className="scheduledFormRow">
          <label>
            Servidor:
            <select name="issabel_server_id" value={form.issabel_server_id} onChange={handleChange} required>
              <option value="">Selecione...</option>
              {servers.map((s) => (
                <option key={s.id} value={s.id.toString()}>{s.name || `PBX ${s.id}`}</option>
              ))}
            </select>
          </label>
          <label>
            E-mail:
            <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="destinatario@exemplo.com" required />
          </label>
        </div>

        <div className="scheduledFormRow">
          <label>
            Frequência:
            <select name="frequencia" value={form.frequencia} onChange={handleChange}>
              <option value="daily">Diária</option>
              <option value="weekly">Semanal</option>
            </select>
          </label>
          <label>
            Hora:
            <input type="time" name="hora" value={form.hora} onChange={handleChange} />
          </label>
          {form.frequencia === 'weekly' && (
            <label>
              Dia da semana:
              <select name="dia_semana" value={form.dia_semana} onChange={handleChange}>
                {DIAS.map((d) => (
                  <option key={d.v} value={d.v}>{d.label}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            Filas (CSV, vazio = todas):
            <input type="text" name="filas" value={form.filas} onChange={handleChange} placeholder="14000,14001" />
          </label>
          <label className="scheduledCheck">
            <input type="checkbox" name="ativo" checked={form.ativo} onChange={handleChange} />
            Ativo
          </label>
        </div>

        <div className="scheduledActions">
          <button type="submit" className="button buttonPrimary">
            {editingId ? 'Salvar alterações' : 'Adicionar agendamento'}
          </button>
          {editingId && (
            <button type="button" className="button buttonSecondary" onClick={cancelEdit}>
              Cancelar
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <div className="loadingMessage">Carregando...</div>
      ) : schedules.length === 0 ? (
        <div className="noDataMessage">Nenhum agendamento configurado.</div>
      ) : (
        <div className="tableContainer">
          <table className="extensionsTable">
            <thead>
              <tr>
                <th>Servidor</th>
                <th>E-mail</th>
                <th>Frequência</th>
                <th>Hora</th>
                <th>Filas</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.id}>
                  <td>{s.issabel_server_name || s.issabel_server_id}</td>
                  <td>{s.email}</td>
                  <td>{s.frequencia === 'weekly' ? `Semanal (${DIAS.find((d) => d.v === Number(s.dia_semana))?.label || s.dia_semana})` : 'Diária'}</td>
                  <td>{s.hora}</td>
                  <td>{s.filas || 'Todas'}</td>
                  <td>{s.ativo ? 'Ativo' : 'Inativo'}</td>
                  <td>
                    <button className="button buttonSecondary buttonSmall" onClick={() => edit(s)}>Editar</button>{' '}
                    <button className="button buttonDanger buttonSmall" onClick={() => remove(s.id)}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ScheduledReports;
